import classNames from 'classnames'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ConnectedReferenceField } from './ConnectedReferenceField'
import { DisplayLabel } from '@/Atomic/Atoms/DisplayLabel'
import { DisplayField } from '@/Atomic/Molecules/DisplayField'
import { CharacterLimitedTextarea } from '@/Atomic/Molecules/Inputs/CharacterLimitedTextarea'
import { DateTimePicker } from '@/Atomic/Molecules/Inputs/DateTimePicker'
import { Input } from '@/Atomic/Molecules/Inputs/Input'
import { Select } from '@/Atomic/Molecules/Inputs/Select/Select'
import { FieldMap } from '@/utils/CRUD/read/dataItem/types'
import { getEnumGroup } from '@/utils/enumGroupVariant'

import './CustomField.scss'

interface FieldProps {
  fieldMap: FieldMap
  className?: string
  isEditing?: boolean
  isReadonly?: boolean
}

const getControllerDefaultValue = (
  type?: FieldMap['type'],
  value?: unknown,
) => {
  if (type === 'enumarray') {
    return Array.isArray(value) ? value : []
  }

  if (
    type === 'boolean' ||
    type === 'enum' ||
    type === 'reference' ||
    type === 'date' ||
    type === 'datetime'
  ) {
    return value ?? null
  }

  return value
}

export const CustomField: React.FC<FieldProps> = ({
  fieldMap,
  className,
  isEditing,
  isReadonly,
}) => {
  const { t } = useTranslation()
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext()
  const {
    fieldKey,
    name,
    title,
    type,
    value,
    displayValue,
    isMultiline,
    enumValueOptions,
    fieldSchema,
    reference: { referenceDataItem, loadReferenceOptions } = {},
  } = fieldMap
  const requirementState = isEditing
    ? fieldSchema?.mandatory
      ? 'required'
      : 'optional'
    : undefined

  const error = errors[name]

  if (!isEditing && !value) return

  if (!isEditing) {
    return (
      <div className="form-row-display">
        <div className="label">
          <DisplayLabel label={title} variant="paragraph" />
        </div>
        <div className="value">
          <DisplayField
            type={type}
            value={displayValue}
            referenceDataItem={referenceDataItem}
            enumGroup={getEnumGroup(fieldSchema?.enumValues, value)}
          />
        </div>
      </div>
    )
  }

  if (type === 'boolean') {
    return (
      <Controller
        name={name}
        control={control}
        defaultValue={getControllerDefaultValue(type, value)}
        render={({ field: { onChange, value: currentValue } }) => (
          <Select
            key={fieldKey}
            name={name}
            label={title}
            requirementState={requirementState}
            options={[
              { value: true, label: t('common.yes') },
              { value: false, label: t('common.no') },
            ]}
            value={currentValue}
            onChange={onChange}
            className={classNames(className)}
            isDisabled={isReadonly}
            error={error?.message?.toString()}
          />
        )}
      />
    )
  }

  if (type === 'enum' || type === 'enumarray') {
    const isMultiSelect = type === 'enumarray'
    const flash = fieldKey.endsWith('-') ? false : true

    return (
      <Controller
        name={name}
        control={control}
        defaultValue={getControllerDefaultValue(type, value)}
        render={({ field: { onChange, value: currentValue } }) => (
          <Select
            key={fieldKey}
            name={name}
            label={title}
            requirementState={requirementState}
            options={enumValueOptions}
            value={currentValue}
            onChange={onChange}
            className={classNames(className, { 'multi-enum': isMultiSelect })}
            isMulti={isMultiSelect}
            flash={flash}
            isDisabled={isReadonly}
            error={error?.message?.toString()}
          />
        )}
      />
    )
  }

  if (type === 'reference') {
    return (
      <Controller
        name={name}
        control={control}
        defaultValue={getControllerDefaultValue(type, value)}
        render={({ field: { onChange, value: currentValue } }) => (
          <ConnectedReferenceField
            name={name}
            label={title}
            requirementState={requirementState}
            displayValue={
              currentValue === value
                ? (displayValue as string | undefined)
                : undefined
            }
            value={currentValue}
            fieldSchema={fieldSchema}
            onChange={onChange}
            loadReferenceOptions={loadReferenceOptions}
            className={className}
            isReadonly={isReadonly}
            error={error?.message?.toString()}
          />
        )}
      />
    )
  }

  if (type === 'date' || type === 'datetime') {
    return (
      <Controller
        name={name}
        control={control}
        defaultValue={getControllerDefaultValue(type, value)}
        render={({ field: { onChange, value: currentValue } }) => (
          <DateTimePicker
            name={name}
            title={title}
            requirementState={requirementState}
            onChange={onChange}
            value={currentValue ?? null}
            className={className}
            disabled={isReadonly}
            clearable={!isReadonly}
            error={error?.message?.toString()}
          />
        )}
      />
    )
  }

  if (type === 'string' && isMultiline) {
    return (
      <CharacterLimitedTextarea
        name={name}
        label={title}
        requirementState={requirementState}
        defaultValue={value as string}
        register={register}
        className={className}
        characterLimit={fieldSchema?.maxLength}
        enableCharacterLimit
        control={control}
        disabled={isReadonly}
        error={error?.message?.toString()}
      />
    )
  }

  return (
    <Input
      name={name}
      label={title}
      requirementState={requirementState}
      register={register}
      defaultValue={value as string}
      className={className}
      disabled={isReadonly}
      error={error?.message?.toString()}
    />
  )
}
